import { maxBy } from "lodash";

import {
  AudienceAction,
  AudienceDto,
  AudienceFiltersDto,
  AudienceType,
  ExpressionPayloadType,
  ExpressionType,
} from "@intelligems/ig-types";
import * as rtk from "@reduxjs/toolkit";
import { type PayloadAction } from "@reduxjs/toolkit";

import {
  initialAudienceFilterState,
  initialAudienceState,
} from "../../api/initials";
import { expressionListToPostFix } from "../../components/Utils/audienceExpressionConversion";
import { initialIAudienceState } from "../types/audience";

const audienceSlice = rtk.createSlice({
  name: "audience",
  initialState: initialIAudienceState,
  reducers: {
    resetAudiences: (draft) => {
      draft = initialIAudienceState;
      return draft;
    },
    initAudiences: (
      draft,
      action: PayloadAction<{
        audience: AudienceDto | undefined | null;
        isCampaignBeingEdited?: boolean;
        priceTestDefaultCurrency?: string;
      }>
    ) => {
      const key = action.payload.isCampaignBeingEdited
        ? "campaignBeingEditedAudience"
        : "audience";
      if (action.payload.audience) {
        draft[key] = action.payload.audience;
        if (!action.payload.isCampaignBeingEdited) {
          draft.storedAudience = action.payload.audience;
        }
      } else {
        draft[key] = initialAudienceState(
          action.payload.priceTestDefaultCurrency
        );
      }
      // should a sort be done here?
      draft.isSuccess = true;
    },
    reorderAudience: (
      draft,
      action: PayloadAction<{
        value: AudienceFiltersDto[];
        isCampaignBeingEdited?: boolean;
      }>
    ) => {
      const key = action.payload.isCampaignBeingEdited
        ? "campaignBeingEditedAudience"
        : "audience";
      if (!draft.isSuccess || !draft[key]) {
        return draft;
      }
      draft[key]!.filters = action.payload.value;
      return draft;
    },
    setAudienceCurrencyValue: (
      draft,
      action: PayloadAction<{ value: string; isCampaignBeingEdited?: boolean }>
    ) => {
      const key = action.payload.isCampaignBeingEdited
        ? "campaignBeingEditedAudience"
        : "audience";
      if (!draft.isSuccess || !draft[key]) {
        return draft;
      }
      const selectedCurrencies =
        draft[key]?.excludeCurrency?.currency?.split(",") || [];
      if (selectedCurrencies.includes(action.payload.value)) {
        // remove from list
        const filtered = selectedCurrencies.filter(
          (currency) => currency !== action.payload.value
        );
        draft[key]!.excludeCurrency.currency =
          filtered.length > 0 ? filtered.toString() : null;
      } else {
        // add to list
        selectedCurrencies.push(action.payload.value);
        draft[key]!.excludeCurrency.currency = selectedCurrencies.toString();
      }
      draft[key]!.excludeCurrency.exclude = !(
        draft[key]!.excludeCurrency.country === undefined &&
        draft[key]!.excludeCurrency.currency === null
      );
      return draft;
    },
    setAudienceCountryValue: (
      draft,
      action: PayloadAction<{ value: string }>
    ) => {
      if (!draft.isSuccess || !draft.audience) {
        return draft;
      }
      const selectedCountries =
        draft.audience?.excludeCurrency?.country?.split(",") || [];
      if (selectedCountries.includes(action.payload.value)) {
        // remove from list
        const filtered = selectedCountries.filter(
          (country) => country !== action.payload.value
        );
        draft.audience.excludeCurrency.country =
          filtered.length > 0 ? filtered.toString() : undefined;
      } else {
        // add to list
        selectedCountries.push(action.payload.value);
        draft.audience.excludeCurrency.country = selectedCountries.toString();
      }
      draft.audience.excludeCurrency.exclude = !(
        draft.audience.excludeCurrency.country === undefined &&
        draft.audience.excludeCurrency.currency === null
      );
      return draft;
    },
    setAudienceElseAction: (
      draft,
      action: PayloadAction<{
        value: AudienceAction;
        isCampaignBeingEdited?: boolean;
      }>
    ) => {
      const key = action.payload.isCampaignBeingEdited
        ? "campaignBeingEditedAudience"
        : "audience";
      if (!draft.isSuccess || !draft[key]) {
        return draft;
      }
      draft[key]!.elseAction = action.payload.value;
      return draft;
    },
    setAudienceVariation: (
      draft,
      action: PayloadAction<{ value: string; isCampaignBeingEdited?: boolean }>
    ) => {
      const key = action.payload.isCampaignBeingEdited
        ? "campaignBeingEditedAudience"
        : "audience";
      if (!draft.isSuccess || !draft[key]) {
        return draft;
      }
      draft[key]!.elseVariationId = action.payload.value;
      return draft;
    },
    setFilterAction: (
      draft,
      action: PayloadAction<{
        value: AudienceAction;
        index: number;
        isCampaignBeingEdited?: boolean;
      }>
    ) => {
      const key = action.payload.isCampaignBeingEdited
        ? "campaignBeingEditedAudience"
        : "audience";
      if (!draft.isSuccess || !draft[key]) {
        return draft;
      }
      draft[key]!.filters[action.payload.index].action = action.payload.value;
      return draft;
    },
    setFilterVariation: (
      draft,
      action: PayloadAction<{
        value: string;
        index: number;
        isCampaignBeingEdited?: boolean;
      }>
    ) => {
      const key = action.payload.isCampaignBeingEdited
        ? "campaignBeingEditedAudience"
        : "audience";
      if (!draft.isSuccess || !draft[key]) {
        return draft;
      }
      draft[key]!.filters[action.payload.index].variationId =
        action.payload.value;
      return draft;
    },
    setElseVariation: (
      draft,
      action: PayloadAction<{ value: string; isCampaignBeingEdited?: boolean }>
    ) => {
      const key = action.payload.isCampaignBeingEdited
        ? "campaignBeingEditedAudience"
        : "audience";
      if (!draft.isSuccess || !draft[key]) {
        return draft;
      }
      draft[key]!.elseVariationId = action.payload.value;
      return draft;
    },
    setElseExcludeAnalytics: (
      draft,
      action: PayloadAction<{ value: boolean; isCampaignBeingEdited?: boolean }>
    ) => {
      const key = action.payload.isCampaignBeingEdited
        ? "campaignBeingEditedAudience"
        : "audience";
      if (!draft.isSuccess || !draft[key]) {
        return draft;
      }
      draft[key]!.elseExcludeFromAnalytics = action.payload.value;
      return draft;
    },
    setFilterExcludeAnalytics: (
      draft,
      action: PayloadAction<{ value: boolean; index: number }>
    ) => {
      if (!draft.isSuccess || !draft.audience) {
        return draft;
      }
      draft.audience.filters[action.payload.index].excludeFromAnalytics =
        action.payload.value;
      return draft;
    },
    setAudience: (
      draft,
      action: PayloadAction<{
        value: AudienceDto;
        isCampaignBeingEdited?: boolean;
      }>
    ) => {
      const key = action.payload.isCampaignBeingEdited
        ? "campaignBeingEditedAudience"
        : "audience";
      if (!draft.isSuccess || !draft[key]) {
        return draft;
      }
      draft[key]! = action.payload.value;
      return draft;
    },
    setAudienceType: (
      draft,
      action: PayloadAction<{
        value: AudienceType;
        isCampaignBeingEdited?: boolean;
      }>
    ) => {
      const key = action.payload.isCampaignBeingEdited
        ? "campaignBeingEditedAudience"
        : "audience";
      if (!draft.isSuccess || !draft[key]) {
        return draft;
      }
      draft[key]!.audienceType = action.payload.value;
      return draft;
    },
    setAudiencePriceInit: (
      draft,
      action: PayloadAction<{
        value: string;
      }>
    ) => {
      if (!draft.isSuccess || !draft.audience) {
        return draft;
      }
      draft.audience.excludeCurrency.exclude = true;
      draft.audience.excludeCurrency.currency = action.payload.value;
      return draft;
    },
    clearAudience: (
      draft,
      action: PayloadAction<{
        isCampaignBeingEdited?: boolean;
      }>
    ) => {
      const key = action.payload.isCampaignBeingEdited
        ? "campaignBeingEditedAudience"
        : "audience";
      if (!draft.isSuccess || !draft[key]) {
        return draft;
      }
      draft[key]! = {
        ...draft[key]!,
        wholesale: false,
        filters: [],
        elseAction: null,
        elseVariationId: null,
        elseExcludeFromAnalytics: false,
      };
      return draft;
    },
    clearAudienceCountryCurrency: (
      draft,
      action: PayloadAction<{
        defaultCurrency?: string;
        isCampaignBeingEdited?: boolean;
      }>
    ) => {
      const key = action.payload.isCampaignBeingEdited
        ? "campaignBeingEditedAudience"
        : "audience";
      if (!draft.isSuccess || !draft[key]) {
        return draft;
      }
      draft[key]!.excludeCurrency.exclude =
        action.payload.defaultCurrency !== undefined;
      draft[key]!.excludeCurrency.currency =
        action.payload.defaultCurrency ?? null;
      draft[key]!.excludeCurrency.country = undefined;
      return draft;
    },
    setAudienceExpression: (
      draft,
      action: PayloadAction<{
        expressionsList: ExpressionPayloadType[];
        filterIndex: number;
        expressionType?: ExpressionType;
        action?: AudienceAction;
        isCampaignBeingEdited?: boolean;
        isPersonalization?: boolean;
      }>
    ) => {
      const key = action.payload.isCampaignBeingEdited
        ? "campaignBeingEditedAudience"
        : "audience";
      if (!draft.isSuccess || !draft[key]) {
        return draft;
      }
      const postfix = expressionListToPostFix(action.payload.expressionsList);
      if (!draft[key]!.filters[action.payload.filterIndex]) {
        draft[key]!.filters.push({
          ...initialAudienceFilterState(),
          priority: action.payload.filterIndex,
          expression: postfix,
          action:
            action.payload.action ||
            (action.payload.isPersonalization
              ? "experienceInclude"
              : "randomVariation"),
          ...(action.payload.expressionType && {
            expressionType: action.payload.expressionType,
          }),
        });
      } else {
        draft[key]!.filters[action.payload.filterIndex].expression = postfix;
        if (
          postfix.length === 0 &&
          draft[key]!.filters.length === 1 &&
          action.payload.filterIndex === 0
        ) {
          draft[key]! = {
            ...draft[key]!,
            enabled: false,
            wholesale: false,
            filters: [],
            elseAction: null,
            elseVariationId: null,
            elseExcludeFromAnalytics: false,
          };
        } else {
          if (action.payload.expressionType)
            draft[key]!.filters[action.payload.filterIndex].expressionType =
              action.payload.expressionType;
          if (action.payload.action)
            draft[key]!.filters[action.payload.filterIndex].action =
              action.payload.action;
        }
      }
      if (!draft[key]!.elseAction)
        draft[key]!.elseAction = "experienceUnassigned";
      return draft;
    },
    deleteFilterRow: (
      draft,
      action: PayloadAction<{ value: number; isCampaignBeingEdited?: boolean }>
    ) => {
      const key = action.payload.isCampaignBeingEdited
        ? "campaignBeingEditedAudience"
        : "audience";
      if (!draft.isSuccess || !draft[key]) {
        return draft;
      }
      draft[key]!.filters.splice(action.payload.value, 1);
      draft[key]!.filters = draft[key]!.filters.map((item, i) => {
        return {
          ...item,
          priority: i,
        };
      });
      if (draft[key]!.filters.length === 0) {
        draft[key]!.elseAction = null;
        draft[key]!.elseVariationId = null;
        draft[key]!.elseExcludeFromAnalytics = false;
        // draft.audience.elseTestGroupName = null;
      }
      return draft;
    },
    updateAudienceRow: (
      draft,
      action: PayloadAction<{
        state: AudienceFiltersDto;
        index: number;
        isCampaignBeingEdited?: boolean;
      }>
    ) => {
      const key = action.payload.isCampaignBeingEdited
        ? "campaignBeingEditedAudience"
        : "audience";
      if (!draft.isSuccess || !draft[key]) {
        return draft;
      }
      if (action.payload.index >= 0) {
        draft[key]!.filters[action.payload.index] = action.payload.state;
      } else {
        draft[key]!.filters.push({
          ...action.payload.state,
          priority:
            draft[key]!.filters.length === 0
              ? 0
              : maxBy(draft[key]!.filters, "priority")!.priority + 1,
        });
      }
      return draft;
    },
  },
  selectors: {
    selectedAudienceState: (state) => state,
    selectedAudience: (state) => state.audience as AudienceDto,
    selectedCampaignAudience: (state) =>
      state.campaignBeingEditedAudience as AudienceDto,
  },
});

export const {
  resetAudiences,
  initAudiences,
  reorderAudience,
  deleteFilterRow,
  setAudienceElseAction,
  setAudienceVariation,
  setFilterAction,
  setFilterVariation,
  setElseExcludeAnalytics,
  setElseVariation,
  setAudienceType,
  clearAudience,
  setAudienceCurrencyValue,
  setAudienceCountryValue,
  clearAudienceCountryCurrency,
  updateAudienceRow,
  setAudienceExpression,
  setAudience,
  setFilterExcludeAnalytics,
  setAudiencePriceInit,
} = audienceSlice.actions;

export const audienceReducer = audienceSlice.reducer;
export const {
  selectedAudienceState,
  selectedAudience,
  selectedCampaignAudience,
} = audienceSlice.selectors;
